import React from 'react';
import { useStore } from 'react-redux';
import { DriverRegistrationPortalNoAuthService } from '@gen/driverRegistration/driverRegistrationPortalNoAuth';
import { useLocation } from 'react-router-dom';
import { DriverRegistrationLogStatus } from '@gen/partnerDriverWeb/driverRegistrationPortal/models/DriverRegistrationLogStatus';
import { useDispatch, useSelector } from '../../redux/store';
import {
  getHash,
  getRegistrationStatus,
  getSteps,
  getTranslations,
  isFormUploading,
} from '../../redux/form/formSelectors';
import { getCommonErrors } from '../../redux/notification/notificationsSelectors';
import { getScriptDirection } from '../../redux/localization/localizationSelectors';
import { checkShowField } from '../../common/fieldConditions';
import { Field } from '../Field';
import { setFormFieldValue, setFormStep } from '../../redux/form/formActions';
import { clearFieldError } from '../../redux/notification/notificationActions';
import {
  autosaveHandleBlur,
  autosaveHandleBlurOnChangedValue,
  autosaveHandleUpdate,
} from '../../redux/autosave/autosaveThunk';
import { postForm } from '../../redux/form/formThunk';
import { Button } from '../Button';
import { IAppState } from '../../redux/types';
import { AutosaveFieldStatus } from '../AutosaveFieldStatus';
import { getDeeplinkScheme } from '../../common/environment';
import { Loader } from '../common/Loader';
import MarkdownWrapper from '../common/MarkdownWrapper';

const isFieldFilled = (
  field: DriverRegistrationPortalNoAuthService.Field
): boolean => {
  const isCheckboxField =
    field.type ===
      DriverRegistrationPortalNoAuthService.FrontendFieldDefinitionType
        .CHECKBOX ||
    field.type ===
      DriverRegistrationPortalNoAuthService.FrontendFieldDefinitionType
        .GENERIC_CHECKBOX;

  if (isCheckboxField) {
    return true;
  }

  const value = field.current_value;

  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.values(value).some((item) => {
      if (item === null || item === undefined) {
        return false;
      }
      if (typeof item === 'string') {
        return item.trim().length > 0;
      }
      if (Array.isArray(item)) {
        return item.length > 0;
      }
      return true;
    });
  }

  return true;
};

export const DriverSignupMinimal: React.FC = () => {
  const dispatch = useDispatch();
  const store = useStore<IAppState>();
  const hash = useSelector(getHash);
  const commonTranslations = useSelector(getTranslations);
  const isUploading = useSelector(isFormUploading);
  const commonErrors = useSelector(getCommonErrors);
  const scriptDirection = useSelector(getScriptDirection);
  const location = useLocation();
  const category = new URLSearchParams(location.search).get('id');
  const registrationStatus = useSelector(getRegistrationStatus);

  const steps = useSelector(getSteps);
  const currentStep = steps.find((s) => s.category === category);

  let hasPageLoadFailed = false;

  // Data is loaded but current step is not found
  if (!currentStep && steps.length > 0) {
    hasPageLoadFailed = true;
  }

  // This page should only work for in-progress registrations
  if (registrationStatus !== DriverRegistrationLogStatus.IN_PROGRESS) {
    hasPageLoadFailed = true;
  }

  React.useEffect(() => {
    if (currentStep?.id) {
      dispatch(setFormStep(currentStep.id));
    }
  }, [dispatch, currentStep]);

  React.useEffect(() => {
    document.documentElement.dir = scriptDirection;
  }, [scriptDirection]);

  const fieldAutosaveBlurHandler = (
    config: {
      enabled: boolean;
      always?: boolean;
    },
    field: DriverRegistrationPortalNoAuthService.Field
  ) => {
    if (!config.enabled) {
      return;
    }
    if (config.always) {
      dispatch(autosaveHandleBlur(field));
    } else {
      dispatch(autosaveHandleBlurOnChangedValue());
    }
  };

  const visibleFields = React.useMemo(() => {
    if (!currentStep) {
      return [];
    }
    return currentStep.fields
      .filter((field) => {
        const showField = checkShowField(field, steps);
        const isDocumentField =
          field.type ===
          DriverRegistrationPortalNoAuthService.FrontendFieldDefinitionType
            .DOCUMENT_UPLOAD;
        return showField && !isDocumentField;
      })
      .map((field, index) => ({
        step: currentStep,
        field,
        key:
          field.generic_field_key || `${currentStep.id}-${field.name}-${index}`,
      }));
  }, [currentStep, steps]);

  if (currentStep && visibleFields?.length === 0) {
    hasPageLoadFailed = true;
  }

  const mappedFields = visibleFields.map(({ step, field, key }) => {
    return (
      <Field
        fieldDefinition={field}
        commonTranslations={commonTranslations}
        step={step}
        hash={hash}
        updateHandler={({ changed, autosaveEnabled }) => {
          dispatch(setFormFieldValue(step, field, changed));
          dispatch(clearFieldError(key));
          if (autosaveEnabled) {
            dispatch(autosaveHandleUpdate(field, changed));
          }
        }}
        blurHandler={(_e, autosave) =>
          fieldAutosaveBlurHandler(autosave, field)
        }
        key={key}
      />
    );
  });

  const isFormComplete = React.useMemo(
    () =>
      visibleFields.some(({ field }) => field.is_required) &&
      visibleFields
        .filter(({ field }) => field.is_required)
        .every(({ field }) => isFieldFilled(field)),
    [visibleFields]
  );

  const buttonLabel = React.useMemo(() => {
    if (isUploading) {
      return '';
    }
    return isFormComplete
      ? commonTranslations.common_actions.continue
      : commonTranslations.common_actions.save;
  }, [isUploading, commonTranslations, isFormComplete]);

  const submitHandler = React.useCallback(async () => {
    if (!currentStep) {
      return;
    }
    await dispatch(postForm(currentStep, false, currentStep.category));

    const hasFieldValidationErrors =
      store.getState().notifications.fieldErrors.length > 0;
    const hasCommonErrors =
      store.getState().notifications.commonErrors.length > 0;
    if (!hasFieldValidationErrors && !hasCommonErrors) {
      window.location.replace(`${getDeeplinkScheme()}action/closeBrowser`);
    }
  }, [dispatch, currentStep, store]);

  // Error screen
  if (hasPageLoadFailed) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-center text-gray-700 dark:text-gray-300 text-lg w-80 mx-auto pb-10">
          {commonTranslations.common_errors.something_went_wrong}
        </p>
      </div>
    );
  }

  // Loading state
  if (!currentStep || visibleFields.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen -mt-6">
        <Loader
          data-test="app_form_loader"
          textColor="text-[#E5E7EB] dark:text-[#424B5D]"
          id="document_upload_example_loader"
        />
      </div>
    );
  }

  return (
    <div className="pb-20">
      <div className="text-left rtl:text-right tablet:text-center tablet:rtl:text-center mx-auto">
        <AutosaveFieldStatus commonTranslations={commonTranslations} />
        <h1 className="mt-5 font-bold text-2xl">
          <MarkdownWrapper>{currentStep.translations.title}</MarkdownWrapper>
        </h1>
        {currentStep.translations.subtitle && (
          <div className="text-gray-700 dark:text-gray-300 text-sm mt-1">
            <MarkdownWrapper>
              {currentStep.translations.subtitle}
            </MarkdownWrapper>
          </div>
        )}
        <div className="mb-10" />
      </div>
      {mappedFields}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-dark-background border-gray-200 dark:border-gray-800 pb-4">
        <div className="tablet:max-w-screen-tablet desktop:max-w-xl pl-6 pr-6 mx-auto w-full">
          {commonErrors.length > 0 && (
            <p className="text-red-500 text-sm text-center mt-4 mb-2 dark:text-dark-red">
              {commonTranslations.common_errors.something_went_wrong}
            </p>
          )}
          <Button
            onClick={submitHandler}
            color={isUploading ? 'bg-green-200' : 'bg-green-500'}
            loadingTextColor="text-green-500"
            label={buttonLabel}
            disabled={isUploading}
            loading={isUploading}
            id="driver_signup_minimal_submit"
            fullWidth
          />
        </div>
      </div>
    </div>
  );
};
